import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  CssBaseline,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Fade,
  CircularProgress,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Avatar
} from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import TranslateIcon from '@mui/icons-material/Translate';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Estimator, MaterialEstimator } from './components/estimator';
import Settings from './components/Settings';
import AuthPage from './components/auth/AuthPage';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import LandingPage from './components/landing/LandingPage';
import { AccountPage } from './components/account';
import { LanguageProvider, useLanguage } from './utils/LanguageContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { useAuth } from './utils/authUtils';
import PromoToast from './components/promo/PromoToast';
import GuestSignupPopup from './components/promo/GuestSignupPopup';

// Main App component wrapped with providers
function App() {
  return (
    <LanguageProvider>
      <SubscriptionProvider>
        <AppContent />
      </SubscriptionProvider>
    </LanguageProvider>
  );
}

// App content that uses the language context
function AppContent() {
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [currentView, setCurrentView] = useState('materials'); // 'materials', 'budget', 'settings'
  const [isLoading, setIsLoading] = useState(true);
  const { language, changeLanguage } = useLanguage();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Capture referral code from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) localStorage.setItem('rumahkira_referral_code', ref.toUpperCase());
  }, []);

  // Loading splash screen
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Handle navigation state from landing page or ConversionModal
  useEffect(() => {
    if (location.state?.view === 'budget') {
      setCurrentView('budget');
    } else if (location.state?.view === 'account') {
      setCurrentView('account');
    } else if (location.state?.category) {
      setCurrentView('materials');
      // Category selection will be handled by MaterialEstimator
    }
  }, [location.state]);

  // Creative Tim Soft UI Design System — adapted for web
  const bg = darkMode ? '#141727' : '#E9ECEF';
  const bgCard = darkMode ? '#1E2139' : '#FFFFFF';
  const softShadow = darkMode
    ? '0 7px 14px rgba(0, 0, 0, 0.25)'
    : '0 7px 14px rgba(0, 0, 0, 0.07)';
  const softShadowSm = darkMode
    ? '0 4px 7px rgba(0, 0, 0, 0.2)'
    : '0 4px 7px rgba(0, 0, 0, 0.05)';
  const softShadowLg = darkMode
    ? '0 10px 24px rgba(0, 0, 0, 0.35)'
    : '0 10px 24px rgba(0, 0, 0, 0.1)';
  const softInset = darkMode
    ? 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
    : 'inset 0 2px 4px rgba(0, 0, 0, 0.06)';

  // Primary gradient (indigo/purple — brand-aligned variant of Creative Tim's pink→purple)
  const primaryGradient = 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)';
  const infoGradient = 'linear-gradient(135deg, #21D4FD 0%, #2152FF 100%)';
  const successGradient = 'linear-gradient(135deg, #98EC2D 0%, #17AD37 100%)';
  const dangerGradient = 'linear-gradient(135deg, #FF667C 0%, #EA0606 100%)';
  const darkGradient = darkMode
    ? 'linear-gradient(135deg, #2D3154 0%, #141727 100%)'
    : 'linear-gradient(135deg, #3A416F 0%, #141727 100%)';

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#818CF8' : '#6366F1',
        light: darkMode ? '#A5B4FC' : '#818CF8',
        dark: darkMode ? '#6366F1' : '#4F46E5',
        lighter: darkMode ? 'rgba(129, 140, 248, 0.15)' : 'rgba(99, 102, 241, 0.1)',
      },
      secondary: {
        main: darkMode ? '#94A3B8' : '#627594',
        light: darkMode ? '#CBD5E1' : '#A8B8D8',
      },
      background: {
        default: bg,
        paper: bgCard,
      },
      text: {
        primary: darkMode ? '#E2E8F0' : '#252F40',
        secondary: darkMode ? '#94A3B8' : '#627594',
      },
      divider: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.06)',
      error: {
        main: '#EA0606',
        light: '#FF667C',
        lighter: darkMode ? 'rgba(234, 6, 6, 0.15)' : 'rgba(234, 6, 6, 0.08)',
      },
      warning: {
        main: '#FFC107',
      },
      success: {
        main: '#82D616',
      },
      info: {
        main: '#17C1E8',
      },
    },
    typography: {
      fontFamily: [
        '"Open Sans"',
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'sans-serif',
      ].join(','),
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.005em',
      },
      subtitle1: {
        fontWeight: 500,
      },
      body1: {
        lineHeight: 1.6,
      },
      body2: {
        lineHeight: 1.5,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 16,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: bg,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            padding: '10px 24px',
          },
          contained: {
            background: primaryGradient,
            boxShadow: softShadow,
            border: 'none',
            color: '#FFFFFF',
            '&:hover': {
              boxShadow: softShadowLg,
              background: primaryGradient,
              opacity: 0.9,
            },
            '&:active': {
              boxShadow: softShadowSm,
            },
          },
          containedError: {
            background: dangerGradient,
          },
          containedSuccess: {
            background: successGradient,
          },
          containedInfo: {
            background: infoGradient,
          },
          outlined: {
            borderColor: darkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(0, 0, 0, 0.12)',
            backgroundColor: bgCard,
            boxShadow: softShadowSm,
            '&:hover': {
              boxShadow: softShadow,
              backgroundColor: bgCard,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 16,
            boxShadow: softShadow,
            backgroundColor: bgCard,
          },
          outlined: {
            border: `1px solid ${darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
            boxShadow: softShadowSm,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: softShadow,
            borderRadius: 16,
            backgroundColor: bgCard,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              backgroundColor: darkMode ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
              boxShadow: softInset,
              '& fieldset': {
                borderColor: darkMode ? 'rgba(148, 163, 184, 0.15)' : 'rgba(0, 0, 0, 0.08)',
              },
              '&:hover fieldset': {
                borderColor: darkMode ? 'rgba(148, 163, 184, 0.3)' : 'rgba(0, 0, 0, 0.15)',
              },
              '&.Mui-focused fieldset': {
                borderColor: darkMode ? '#818CF8' : '#6366F1',
                borderWidth: '1.5px',
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: darkMode ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
            boxShadow: softInset,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? 'rgba(148, 163, 184, 0.15)' : 'rgba(0, 0, 0, 0.08)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? 'rgba(148, 163, 184, 0.3)' : 'rgba(0, 0, 0, 0.15)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? '#818CF8' : '#6366F1',
              borderWidth: '1.5px',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
          outlined: {
            boxShadow: softShadowSm,
          },
          filled: {
            boxShadow: softShadowSm,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 8,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            '& .MuiTabs-indicator': {
              borderRadius: 4,
              height: 3,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: softShadowSm,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            boxShadow: softShadowLg,
            borderRadius: 16,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: '16px 0 0 16px',
            backgroundColor: bgCard,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0, 0, 0, 0.06)',
            height: 6,
          },
          bar: {
            borderRadius: 4,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            boxShadow: softShadow,
            borderRadius: 16,
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            borderRadius: 14,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            boxShadow: softShadowSm,
          },
        },
      },
    },
  });

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
  };

  const handleLanguageToggle = () => {
    changeLanguage(language === 'en' ? 'my' : 'en');
  };

  const content = {
    en: {
      title: 'RumahKira',
      subtitle: 'Material Estimator',
      home: 'Materials',
      homeDesc: 'Calculate material quantities',
      budget: 'Budget',
      budgetDesc: 'Estimate project costs',
      account: 'Account',
      accountDesc: 'Profile & subscription',
      settings: 'Settings',
      settingsDesc: 'App preferences',
      signOut: 'Logout',
      signOutDesc: 'Sign out of account',
      menu: 'Menu',
      welcome: 'Welcome',
      guest: 'Guest User',
      upgrade: 'Upgrade'
    },
    my: {
      title: 'RumahKira',
      subtitle: 'Penganggar Bahan',
      home: 'Bahan',
      homeDesc: 'Kira kuantiti bahan',
      budget: 'Bajet',
      budgetDesc: 'Anggar kos projek',
      account: 'Akaun',
      accountDesc: 'Profil & langganan',
      settings: 'Tetapan',
      settingsDesc: 'Keutamaan aplikasi',
      signOut: 'Log Keluar',
      signOutDesc: 'Log keluar akaun',
      menu: 'Menu',
      welcome: 'Selamat Datang',
      guest: 'Pengguna Tetamu',
      upgrade: 'Naik Taraf'
    }
  };

  const txt = content[language] || content.en;

  // Check if mobile
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleNavClick = (view) => {
    if (view === 'logout') {
      signOut();
      navigate('/'); // Redirect to landing page after logout
    } else {
      setCurrentView(view);
    }
    setDrawerOpen(false);
  };

  // Render current view
  const renderView = () => {
    switch (currentView) {
      case 'budget':
        return <Estimator language={language} darkMode={darkMode} />;
      case 'account':
        return <AccountPage />;
      case 'settings':
        return <Settings darkMode={darkMode} toggleDarkMode={toggleDarkMode} />;
      default:
        return <MaterialEstimator language={language} initialCategory={location.state?.category} />;
    }
  };

  // Navigation items configuration
  const navItems = [
    { id: 'materials', label: txt.home, desc: txt.homeDesc, icon: <HomeIcon /> },
    { id: 'budget', label: txt.budget, desc: txt.budgetDesc, icon: <AccountBalanceWalletIcon /> },
    { id: 'account', label: txt.account, desc: txt.accountDesc, icon: <AccountCircleIcon /> },
    { id: 'settings', label: txt.settings, desc: txt.settingsDesc, icon: <SettingsIcon /> },
  ];

  // Mobile Drawer Content
  const DrawerContent = () => (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: bgCard }}>
      {/* Drawer Header */}
      <Box sx={{
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ background: primaryGradient, width: 40, height: 40, boxShadow: softShadowSm }}>
            {user ? user.name?.charAt(0).toUpperCase() || <PersonIcon /> : <PersonIcon />}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {user ? user.name || user.email : txt.guest}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user ? user.email : txt.welcome}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={toggleDrawer(false)} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <List sx={{ flex: 1, py: 1.5 }}>
        {navItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={currentView === item.id}
              onClick={() => handleNavClick(item.id)}
              sx={{
                mx: 1.5,
                my: 0.5,
                borderRadius: 2,
                '&.Mui-selected': {
                  background: primaryGradient,
                  color: '#FFFFFF',
                  boxShadow: softShadowSm,
                  '&:hover': {
                    background: primaryGradient,
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#FFFFFF',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: currentView === item.id ? '#FFFFFF' : 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.desc}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                secondaryTypographyProps={{
                  fontSize: '0.75rem',
                  sx: { color: currentView === item.id ? 'rgba(255,255,255,0.75)' : 'text.secondary' }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Drawer Footer */}
      <Box sx={{ p: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
        {/* Utility toggles */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 2 }}>
          <Tooltip title={language === 'en' ? 'Bahasa Malaysia' : 'English'}>
            <IconButton onClick={handleLanguageToggle} sx={{ bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}>
              <TranslateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={darkMode ? 'Light Mode' : 'Dark Mode'}>
            <IconButton onClick={toggleDarkMode} sx={{ bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}>
              {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Logout Button */}
        {user && (
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={() => handleNavClick('logout')}
            sx={{ borderRadius: 2 }}
          >
            {txt.signOut}
          </Button>
        )}
      </Box>
    </Box>
  );

  const MainLayout = () => (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      pt: '72px', // Space for fixed navbar
    }}>
      {/* Floating Navbar - Same style as Landing Page */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1100,
          width: 'calc(100% - 24px)',
          maxWidth: 800,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            px: 2,
            borderRadius: 3,
            bgcolor: bgCard,
            boxShadow: softShadow,
          }}
        >
          {/* Logo */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => handleNavClick('materials')}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: primaryGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: softShadowSm,
              }}
            >
              <HomeWorkIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
                {txt.title}<sup style={{ fontSize: '0.5em' }}>&trade;</sup>
              </Typography>
            </Box>
          </Box>

          {/* Center: Navigation Items (Desktop) */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {navItems.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  startIcon={item.icon}
                  size="small"
                  sx={{
                    px: 2,
                    py: 0.75,
                    borderRadius: 2,
                    color: currentView === item.id ? '#FFFFFF' : 'text.secondary',
                    background: currentView === item.id ? primaryGradient : 'transparent',
                    fontWeight: currentView === item.id ? 600 : 500,
                    fontSize: '0.85rem',
                    boxShadow: currentView === item.id ? softShadowSm : 'none',
                    '&:hover': {
                      background: currentView === item.id
                        ? primaryGradient
                        : darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                      boxShadow: currentView === item.id ? softShadow : 'none',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Right: Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip title={language === 'en' ? 'Bahasa Malaysia' : 'English'}>
              <IconButton
                onClick={handleLanguageToggle}
                size="small"
                sx={{
                  bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  '&:hover': { bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)' },
                }}
              >
                <TranslateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={darkMode ? 'Light Mode' : 'Dark Mode'}>
              <IconButton
                onClick={toggleDarkMode}
                size="small"
                sx={{
                  bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  '&:hover': { bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)' },
                }}
              >
                {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* Desktop: Logout button */}
            {!isMobile && user && (
              <Tooltip title={txt.signOut}>
                <IconButton
                  onClick={() => handleNavClick('logout')}
                  size="small"
                  sx={{
                    color: 'error.main',
                    bgcolor: darkMode ? 'rgba(234, 6, 6, 0.12)' : 'rgba(234, 6, 6, 0.08)',
                    '&:hover': { bgcolor: darkMode ? 'rgba(234, 6, 6, 0.2)' : 'rgba(234, 6, 6, 0.12)' },
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Mobile: Hamburger Menu */}
            {isMobile && (
              <IconButton
                onClick={toggleDrawer(true)}
                size="small"
                sx={{
                  bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  '&:hover': { bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)' },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            bgcolor: bgCard,
            borderRadius: '16px 0 0 16px',
            boxShadow: softShadowLg,
          }
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Main Content */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
      }}>
        <Fade in={true} timeout={300}>
          <Box>
            {renderView()}
          </Box>
        </Fade>
      </Box>

      {/* Promo / Signup popups (mutually exclusive by user state) */}
      <PromoToast />
      <GuestSignupPopup />
    </Box>
  );

  // Splash Screen Component
  const SplashScreen = () => (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: bg,
        gap: 3,
      }}
    >
      <Fade in={true} timeout={500}>
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 4,
              background: primaryGradient,
              boxShadow: softShadow,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <HomeWorkIcon
              sx={{
                fontSize: 40,
                color: '#FFFFFF',
              }}
            />
          </Box>
          <Typography
            variant="h4"
            fontWeight={800}
            color="text.primary"
            sx={{ letterSpacing: '-0.02em' }}
          >
            RumahKira<sup style={{ fontSize: '0.4em' }}>&trade;</sup>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5 }}
          >
            by Kondingau Community Insight
          </Typography>
        </Box>
      </Fade>
      <CircularProgress
        size={24}
        thickness={4}
        sx={{ color: 'primary.main', mt: 2 }}
      />
    </Box>
  );

  // Show splash screen while loading
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SplashScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/" element={<LandingPage darkMode={darkMode} toggleDarkMode={toggleDarkMode} />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/app/*" element={<MainLayout />} />
      </Routes>
      {/* Gate modals are now handled per-component via useGate hook */}
    </ThemeProvider>
  );
}

export default App;
